import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { ConfirmDialog, ArchiveConfirmDialog } from './Modal';
import Icon from './Icon';
import { getAccessToken, isSecretaryContextUser } from '../authStorage';
import { deleteOperationalPlan, fetchOperationalPlans } from '../operationalPlansApi';
import { archiveEntity } from '../archiveApi';
import { openOperationalPlanPdfInNewTab } from '../operationalPlanPdfExport';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import { useBranchFilter } from '../branchFilter';
import './BranchesPage.css';
import './BoardPage.css';

export default function OperationalPlansPage() {
  const { direction } = useLanguage();
  const t = useT();

  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/officer') ? '/officer' : '/app';
  const { selectedBranch: globalBranch } = useBranchFilter();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [branchFilter, setBranchFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [exportingId, setExportingId] = useState(null);
  const [archiveConfirmId, setArchiveConfirmId] = useState(null);
  const [archiving, setArchiving] = useState(false);

  useEffect(() => {
    if (!getAccessToken()) {
      navigate('/login', { replace: true });
      return;
    }
    if (!isSecretaryContextUser()) {
      setAccessDenied(true);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const data = await fetchOperationalPlans();
        if (!cancelled) setRows(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : t('plans.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const statusLabel = (s) => {
    if (s === 'draft') return t('common.draft');
    if (s === 'under_review') return t('plans.underReview');
    if (s === 'approved') return t('common.approved');
    if (s === 'in_progress') return t('common.inProgress');
    if (s === 'completed') return t('common.completed');
    return s ?? '—';
  };

  const effectiveBranch = globalBranch !== 'all' ? globalBranch : branchFilter;

  const filtered = useMemo(() => rows.filter((x) => {
    const q = search.trim().toLowerCase();
    const qOk = !q
      || (x.title || '').toLowerCase().includes(q)
      || (x.companyName || '').toLowerCase().includes(q)
      || (x.departmentName || '').toLowerCase().includes(q);
    const bOk = effectiveBranch === 'all' || x.companyName === effectiveBranch;
    return qOk && bOk;
  }), [rows, search, effectiveBranch]);

  const fmtDate = (d) => {
    if (!d) return '—';
    return String(d).slice(0, 10);
  };

  const openDelete = (row) => { setDeleteTarget(row); setConfirmOpen(true); };
  const handleExportPdf = async (row) => {
    setExportingId(row.id);
    setError('');
    try {
      await openOperationalPlanPdfInNewTab(row.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('common.errorLoad'));
    } finally {
      setExportingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteOperationalPlan(deleteTarget.id);
      setRows((prev) => prev.filter((x) => x.id !== deleteTarget.id));
      setConfirmOpen(false);
      setDeleteTarget(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('common.errorDelete'));
      setConfirmOpen(false);
      setDeleteTarget(null);
    }
  };

  if (accessDenied) return <p style={{ padding: 24, color: '#78909c', fontFamily: 'Cairo, sans-serif' }} dir={direction}>{t('common.noPerm')}</p>;

  return (
    <div className="page" dir={direction}>
      <div className="list-page-card list-page-card--unified">
        <header className="list-page-card__header">
          <div className="list-page-card__titles">
            <span className="list-page-card__title"><Icon name="report" size={18} color="#00334a" style={{ verticalAlign: 'middle', marginLeft: 6 }} />{t('plans.title')}</span>
            <span className="list-page-card__sub">{t('plans.subtitle')}</span>
          </div>
          <div className="list-page-card__actions">
            <button type="button" className="btn btn--ghost" onClick={() => navigate(`${basePath}/archive?type=OperationalPlan`)}><Icon name="archive" size={14} /> {t('nav.archive')}</button>
            <button type="button" className="btn btn--primary" onClick={() => navigate(`${basePath}/operational-plans/new`)}><Icon name="plus" size={14} color="#fff" /> {t('plans.new')}</button>
          </div>
        </header>

        {error && <div className="list-page-card__banner list-page-card__banner--error">{error}</div>}
        {loading && <div className="list-page-card__loading">{t('plans.loading')}</div>}

      {!loading && (
        <>
          <div className="page__filters">
            <div className="page__search">
              <Icon name="search" size={14} color="#90a4ae" />
              <input className="page__search-input" placeholder={t('plans.search')} value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <select className="page__filter-select" value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)}>
              <option value="all">{t('common.allCompanies')}</option>
              {Array.from(new Set(rows.map((x) => x.companyName))).filter(Boolean).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div className="table-card">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 42 }}>#</th>
                  <th>{t('plans.colTitle')}</th>
                  <th>{t('common.company')}</th>
                  <th>{t('plans.colDept')}</th>
                  <th>{t('common.date')}</th>
                  <th>{t('plans.colStatus')}</th>
                  <th>{t('common.date')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={8}><div className="empty-state"><div className="empty-state__title">{t('plans.noMatch')}</div></div></td></tr>
                ) : filtered.map((item, idx) => (
                  <tr key={item.id}>
                    <td className="td-num">{idx + 1}</td>
                    <td><strong style={{ color: '#00334a' }}>{item.title}</strong></td>
                    <td>{item.companyName || '—'}</td>
                    <td>{item.departmentName || '—'}</td>
                    <td dir="ltr" style={{ textAlign: 'inherit' }}>{fmtDate(item.periodStartUtc)}{item.periodEndUtc ? <> — {fmtDate(item.periodEndUtc)}</> : ''}</td>
                    <td><span className="users-status users-status--active">{statusLabel(item.status)}</span></td>
                    <td dir="ltr">{fmtDate(item.updatedAtUtc)}</td>
                    <td>
                      <div className="td-actions">
                        <button
                          type="button"
                          className="action-btn action-btn--edit"
                          title={t('plans.export')}
                          disabled={exportingId === item.id}
                          onClick={() => handleExportPdf(item)}
                        >
                          {exportingId === item.id ? '…' : t('plans.export')}
                        </button>
                        <button type="button" className="action-btn action-btn--edit" onClick={() => navigate(`${basePath}/operational-plans/${item.id}/edit`)}><Icon name="edit" size={14} /></button>
                        <button type="button" className="action-btn action-btn--archive" title={t('archive.title')} onClick={() => setArchiveConfirmId(item.id)}><Icon name="archive" size={14} /></button>
                        <button type="button" className="action-btn action-btn--delete" onClick={() => openDelete(item)}><Icon name="trash" size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
        itemName={deleteTarget?.title}
        title={t('common.deleteConfirmTitle')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
      />

      <ArchiveConfirmDialog
        open={!!archiveConfirmId}
        onClose={() => setArchiveConfirmId(null)}
        onConfirm={async () => {
          if (!archiveConfirmId) return;
          setArchiving(true);
          try {
            await archiveEntity('OperationalPlan', archiveConfirmId);
            setRows((p) => p.filter((r) => r.id !== archiveConfirmId));
          } catch { /* ignore */ }
          setArchiving(false);
          setArchiveConfirmId(null);
        }}
        itemName={rows.find((r) => r.id === archiveConfirmId)?.title}
        title={t('archive.title')}
        confirmLabel={t('archive.title')}
        cancelLabel={t('common.cancel')}
        loading={archiving}
      />
    </div>
  );
}
